import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Button, Typography } from '@material-ui/core';

interface PatientSmsConfirmModalProps {
  title?: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  sendTo?: string;
  messageToSend?: string;
}

/**
 * Reusable confirmation modal for SMS notification actions
 * Shows SMS message preview and phone number before sending notification
 * Optimized for iPad tablet usage with large, prominent UI elements
 */
export function PatientSmsConfirmModal({
  title = 'Are you sure?',
  message,
  onConfirm,
  onCancel,
  sendTo,
  messageToSend,
}: PatientSmsConfirmModalProps) {
  return (
    <FullScreenOverlay
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Content>
        <Title>{title}</Title>

        {sendTo && (
          <Message variant="body2">
            <SendToText>{sendTo}</SendToText>
          </Message>
        )}

        {messageToSend && (
          <MessageContainer>
            <MessageText>{messageToSend}</MessageText>
          </MessageContainer>
        )}

        <ButtonGroup>
          <ConfirmButton onClick={onConfirm} variant="contained">
            Send Notification
          </ConfirmButton>
          <CancelButton onClick={onCancel} variant="outlined">
            Cancel
          </CancelButton>
        </ButtonGroup>
      </Content>
    </FullScreenOverlay>
  );
}

const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.backgroundColor.string()};
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  padding: 2rem 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  max-width: 90%;
  width: 100%;
  /* No max-height or overflow to prevent scrolling */
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.02);
  }
`;

const Title = styled(Typography)`
  && {
    font-size: 3rem;
    text-align: center;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    line-height: 1.2;
    animation: ${pulse} 2.5s ease-in-out infinite;
  }
`;

const Message = styled(Typography)`
  && {
    font-size: 1.5rem;
    text-align: center;
    color: rgba(255, 255, 255, 0.95);
    line-height: 1.4;
    font-weight: 400;
  }
`;

const SendToText = styled.span`
  font-weight: 600;
  color: #00bcd4;
`;

const MessageContainer = styled.div`
  position: relative;
  padding: 1.5rem 2rem 2.5rem 2rem;
  border-radius: 1rem;
  margin: 0.5rem auto;
  background-color: ${props => props.theme.textColor.alpha(0.15).string()};
  max-width: 90%;
  text-align: left;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;

  :before {
    content: '';
    width: 0;
    height: 0;
    position: absolute;
    border-left: 12px solid transparent;
    border-right: 12px solid ${props => props.theme.textColor.alpha(0.15).string()};
    border-top: 12px solid ${props => props.theme.textColor.alpha(0.15).string()};
    border-bottom: 12px solid transparent;
    right: 25px;
    bottom: -24px;
  }
`;

const MessageText = styled.div`
  font-size: 1.5rem;
  line-height: 1.4;
  color: white;
  font-weight: 500;
  overflow: hidden;

  /* Multi-line ellipsis - allow up to 5 lines */
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 1rem;
  width: 100%;
  max-width: 700px;
`;

const ConfirmButton = styled(Button)`
  && {
    flex: 1;
    padding: 1.5rem 2rem;
    font-size: 1.75rem;
    font-weight: 600;
    background-color: #00bcd4 !important;
    color: white !important;
    border-radius: 12px;
    text-transform: none;
    min-height: 80px;
    transition: all 0.2s ease;

    &:hover {
      background-color: #00acc1 !important;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 188, 212, 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  }
`;

const CancelButton = styled(Button)`
  && {
    flex: 1;
    padding: 1.5rem 2rem;
    font-size: 1.75rem;
    font-weight: 600;
    background-color: rgba(255, 255, 255, 0.08) !important;
    color: white !important;
    border: 3px solid rgba(255, 255, 255, 0.3) !important;
    border-radius: 12px;
    text-transform: none;
    min-height: 80px;
    transition: all 0.2s ease;

    &:hover {
      background-color: rgba(255, 255, 255, 0.15) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
    }
  }
`;
